import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Settings as SettingsIcon,
  LogOut,
  MessageSquare,
  Edit2,
  Trash2,
  BookmarkPlus,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Select } from "./ui/select";
import { WorkgroupSelect } from "./WorkgroupSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ConfirmDialog } from "./ui/confirm-dialog";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import { StatusTimeline, TimelineItem } from "./StatusTimeline";
import { cn } from "../lib/utils";
import { getToken } from '../firebase';
import { Message as ChatMessageType } from '../types';
import {
  getUserPreferences,
  updateUserPreferences,
  fetchPromptGroups,
  fetchPromptCards,
  createPromptCard,
  updatePromptCard,
  deletePromptCard,
  fetchWorkgroupDetails,
  fetchIntegrationSuggestedPrompts,
  canAccessDevs
} from './firestoreFunctions';
import { getModelDisplayName } from '../utils/modelNames';

interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  content: string;
}

interface Session {
  session_id: string;
  title?: string;
  first_message?: string;
  timestamp?: number;
}

interface Workgroup {
  id: string;
  name: string;
  description?: string;
  workgroup_types?: string[];
}

interface ModelInfo {
  name: string;
  description?: string;
}

interface PromptCard {
  id: string;
  title: string;
  query: string;
  createdAt?: Date;
}

interface PromptGroup {
  id: string;
  name: string;
  cards: string[];
  createdAt?: Date;
}

interface IntegrationSummary {
  id: string;
  name?: string;
  suggested_prompts?: string[];
}

interface ExamplePrompt {
  title: string;
  description?: string;
}

const shuffleArray = <T,>(items: T[]): T[] => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const DEFAULT_EXAMPLE_PROMPTS: ExamplePrompt[] = [
  {
    title: "What is my most valuable opportunity?",
    description: "Analyze sales pipeline data"
  },
  {
    title: "Who haven't I contacted this month?",
    description: "Review CRM contact history"
  },
  {
    title: "Which campaigns have worked best?",
    description: "Compare marketing performance"
  },
  {
    title: "What's our current inventory status?",
    description: "Check stock levels across warehouses"
  }
];

interface DashboardProps {
  user: any;
  onLogout: () => void;
}

const llmUrl = `${location.protocol}//${location.hostname}/api/llm`;
type PreferenceOverrides = Partial<{
  theme: string;
  sendKey: string;
  modelSelection: number;
  ipProtection: boolean;
  queryMode: 'standard' | 'direct';
  ragMode: 'standard' | 'agentic';
  ragK: number;
  directRagMinWords: number;
}>;

export const DashboardIntegrated: React.FC<DashboardProps> = ({ user, onLogout }) => {
  // State
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);

  // Listen for sidebar toggle event from top nav hamburger
  useEffect(() => {
    const handleToggleSidebar = (e: Event) => {
      e.preventDefault();
      setIsSidebarOpen(prev => !prev);
    };

    window.addEventListener('toggle-sidebar-request', handleToggleSidebar);
    return () => window.removeEventListener('toggle-sidebar-request', handleToggleSidebar);
  }, []);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [workgroups, setWorkgroups] = useState<Workgroup[]>([]);
  const [selectedWorkgroup, setSelectedWorkgroup] = useState<string>("");
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [modelSelection, setModelSelection] = useState<number>(0);
  const [modelList, setModelList] = useState<ModelInfo[]>([]);
  const [maxModelOptions, setMaxModelOptions] = useState<number>(0);
  const getModelOptionLabel = (index: number) =>
    getModelDisplayName(index, modelList[index]?.name);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [theme, setTheme] = useState<string>('light');
  const [sendKey, setSendKey] = useState<string>('Enter');
  const [ipProtection, setIpProtection] = useState<boolean>(true);
  const [hasDevAccess, setHasDevAccess] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('Loading'); // Status from SSE
  const [processingSteps, setProcessingSteps] = useState<TimelineItem[]>([]); // Processing steps from SSE
  const [elapsedTime, setElapsedTime] = useState<number>(0); // Elapsed time in ms
  const [personalPrompts, setPersonalPrompts] = useState<PromptCard[]>([]);
  const [workgroupPrompts, setWorkgroupPrompts] = useState<PromptCard[]>([]);
  const [personalGroups, setPersonalGroups] = useState<PromptGroup[]>([]);
  const [workgroupGroups, setWorkgroupGroups] = useState<PromptGroup[]>([]);
  const [showPromptDialog, setShowPromptDialog] = useState<boolean>(false);
  const [editingPrompt, setEditingPrompt] = useState<PromptCard | null>(null);
  const [isWorkgroupPrompt, setIsWorkgroupPrompt] = useState<boolean>(false);
  const [promptTitle, setPromptTitle] = useState<string>("");
  const [promptQuery, setPromptQuery] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [promptToDelete, setPromptToDelete] = useState<{ id: string; isWorkgroup: boolean } | null>(null);
  const [isChatsCollapsed, setIsChatsCollapsed] = useState<boolean>(false);
  const [isPersonalPromptsCollapsed, setIsPersonalPromptsCollapsed] = useState<boolean>(false);
  const [isWorkgroupPromptsCollapsed, setIsWorkgroupPromptsCollapsed] = useState<boolean>(false);
  const [integrationMap, setIntegrationMap] = useState<Record<string, IntegrationSummary>>({});
  const [queryMode, setQueryMode] = useState<'standard' | 'direct'>('standard'); // 'standard' or 'direct' (RAG-only)
  const [ragMode, setRagMode] = useState<'standard' | 'agentic'>('standard'); // 'standard' or 'agentic' RAG processing mode
  const [ragK, setRagK] = useState<number>(5); // Number of RAG chunks to return
  const [directRagMinWords, setDirectRagMinWords] = useState<number>(3); // Minimum words required for direct RAG mode

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  const requestCancelledRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const seventyPercentAlertShownRef = useRef(false);
  const processingStepsRef = useRef<TimelineItem[]>([]);
  const hasDevAccessRef = useRef<boolean>(false);
  const requestStartTimeRef = useRef<number>(0);
  const licensePageUrl = `${window.location.origin}/admin?section=license`;

  // Refs to track current preference values (avoids stale closure issues)
  const prefsRef = useRef({
    theme,
    sendKey,
    modelSelection,
    ipProtection,
    queryMode,
    ragMode,
    ragK,
    directRagMinWords
  });

  // Keep refs in sync with state
  useEffect(() => {
    prefsRef.current = {
      theme,
      sendKey,
      modelSelection,
      ipProtection,
      queryMode,
      ragMode,
      ragK,
      directRagMinWords
    };
  }, [theme, sendKey, modelSelection, ipProtection, queryMode, ragMode, ragK, directRagMinWords]);

  const persistPreferences = useCallback(
    async (overrides: PreferenceOverrides = {}) => {
      if (!user?.uid) {
        return false;
      }

      // Use refs for current values to avoid stale closure issues
      const current = prefsRef.current;

      const finalQueryMode = overrides.queryMode ?? current.queryMode;
      const finalRagMode = overrides.ragMode ?? current.ragMode;

      try {
        await updateUserPreferences(
          user.uid,
          overrides.theme ?? current.theme,
          overrides.sendKey ?? current.sendKey,
          overrides.modelSelection ?? current.modelSelection,
          overrides.ipProtection ?? current.ipProtection,
          finalQueryMode,
          finalRagMode,
          overrides.ragK ?? current.ragK,
          overrides.directRagMinWords ?? current.directRagMinWords
        );
        return true;
      } catch (error) {
        console.error("Error saving preferences:", error);
        return false;
      }
    },
    [user?.uid]
  );

  const handleThemePreferenceChange = (value: string) => {
    setTheme(value);
    void persistPreferences({ theme: value });
  };

  const handleSendKeyPreferenceChange = (value: string) => {
    setSendKey(value);
    void persistPreferences({ sendKey: value });
  };

  const handleModelPreferenceChange = (value: number) => {
    setModelSelection(value);
    void persistPreferences({ modelSelection: value });
  };

  const handleIpProtectionChange = () => {
    const next = !ipProtection;
    setIpProtection(next);
    void persistPreferences({ ipProtection: next });
  };

  const handleQueryModeChange = (value: 'standard' | 'direct') => {
    setQueryMode(value);
    prefsRef.current.queryMode = value; // Update ref immediately to avoid race condition
    void persistPreferences({ queryMode: value });
  };

  const handleRagModeChange = (value: 'standard' | 'agentic') => {
    setRagMode(value);
    prefsRef.current.ragMode = value; // Update ref immediately to avoid race condition
    void persistPreferences({ ragMode: value });
  };

  const handleRagKChange = (value: number) => {
    setRagK(value);
    prefsRef.current.ragK = value; // Update ref immediately to avoid race condition
    void persistPreferences({ ragK: value });
  };

  const handleDirectRagMinWordsChange = (value: number) => {
    setDirectRagMinWords(value);
    prefsRef.current.directRagMinWords = value; // Update ref immediately to avoid race condition
    void persistPreferences({ directRagMinWords: value });
  };
  const showLicenseToast = (message: string, variant: 'warning' | 'danger' = 'warning') => {
    try {
      const toast = document.createElement('div');
      toast.className = `fixed bottom-4 left-4 max-w-sm text-sm text-white px-4 py-3 rounded shadow-lg z-[9999] license-limit-toast ${variant === 'danger' ? 'bg-red-600' : 'bg-amber-500'}`;
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s ease';
      toast.style.pointerEvents = 'auto';
      toast.innerHTML = message;
      document.body.appendChild(toast);
      requestAnimationFrame(() => {
        toast.style.opacity = '1';
      });
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.addEventListener('transitionend', () => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, { once: true });
      }, 6000);
    } catch (error) {
      console.error("Failed to show license toast", error);
    }
  };

  // Load collapse states from localStorage on mount
  useEffect(() => {
    const chatsCollapsed = localStorage.getItem('chats-collapsed') === 'true';
    const personalCollapsed = localStorage.getItem('personal-prompts-collapsed') === 'true';
    const workgroupCollapsed = localStorage.getItem('workgroup-prompts-collapsed') === 'true';

    setIsChatsCollapsed(chatsCollapsed);
    setIsPersonalPromptsCollapsed(personalCollapsed);
    setIsWorkgroupPromptsCollapsed(workgroupCollapsed);
  }, []);

  // Save collapse states to localStorage
  useEffect(() => {
    localStorage.setItem('chats-collapsed', isChatsCollapsed.toString());
  }, [isChatsCollapsed]);

  useEffect(() => {
    localStorage.setItem('personal-prompts-collapsed', isPersonalPromptsCollapsed.toString());
  }, [isPersonalPromptsCollapsed]);

  useEffect(() => {
    localStorage.setItem('workgroup-prompts-collapsed', isWorkgroupPromptsCollapsed.toString());
  }, [isWorkgroupPromptsCollapsed]);

  const navigate = useNavigate();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    let cancelled = false;

    const evaluateDevAccess = async () => {
      const uid = user?.uid;
      if (!uid) {
        if (!cancelled) {
          setHasDevAccess(false);
        }
        return;
      }

      try {
        const allowed = await canAccessDevs(uid);
        if (!cancelled) {
          setHasDevAccess(Boolean(allowed));
        }
      } catch (error) {
        if (!cancelled) {
          setHasDevAccess(false);
        }
      }
    };

    evaluateDevAccess();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Fetch workgroups
  useEffect(() => {
    const loadWorkgroups = async () => {
      try {
        const details = await fetchWorkgroupDetails(user.uid);
        const workgroupObjs: Workgroup[] = details.map((wg) => ({
          id: wg.id,
          name: wg.name || wg.id,
          description: wg.description,
          workgroup_types: Array.isArray(wg.workgroup_types) ? wg.workgroup_types : [],
        }));
        setWorkgroups(workgroupObjs);

        // Set default workgroup
        const storedWorkgroup = localStorage.getItem('selectedWorkgroupId');
        if (storedWorkgroup && workgroupObjs.some(wg => wg.id === storedWorkgroup)) {
          setSelectedWorkgroup(storedWorkgroup);
        } else if (workgroupObjs.length > 0) {
          setSelectedWorkgroup(workgroupObjs[0].id);
        }
      } catch (err) {
        console.error("Failed to fetch workgroups:", err);
        setError("Failed to load workgroups");
      }
    };

    loadWorkgroups();
  }, [user.uid]);

  useEffect(() => {
    if (!user?.uid || !selectedWorkgroup) return;
    let isCancelled = false;

    const loadIntegrationPrompts = async () => {
      const current = workgroups.find((wg) => wg.id === selectedWorkgroup);
      const enabledIntegrations = Array.isArray(current?.workgroup_types)
        ? current.workgroup_types.filter((entry) => typeof entry === 'string' && entry.trim() !== '')
        : [];

      if (enabledIntegrations.length === 0) {
        setIntegrationMap({});
        return;
      }

      try {
        const results = await Promise.all(
          enabledIntegrations.map(async (integrationId) => {
            try {
              const data = await fetchIntegrationSuggestedPrompts(
                integrationId,
                selectedWorkgroup
              );
              return {
                id: integrationId,
                name:
                  data && typeof data.name === 'string' && data.name.trim() !== ''
                    ? data.name
                    : integrationId,
                suggested_prompts: Array.isArray(data?.suggested_prompts)
                  ? data.suggested_prompts
                  : [],
              } as IntegrationSummary;
            } catch (error) {
              console.error(
                `DashboardIntegrated: failed to load suggested prompts for ${integrationId}`,
                error
              );
              return null;
            }
          })
        );

        if (isCancelled) return;

        const map = results
          .filter((entry): entry is IntegrationSummary => Boolean(entry))
          .reduce<Record<string, IntegrationSummary>>((acc, integration) => {
            acc[integration.id] = integration;
            return acc;
          }, {});

        setIntegrationMap(map);
      } catch (error) {
        if (!isCancelled) {
          console.error("DashboardIntegrated: failed to load suggested prompts", error);
          setIntegrationMap({});
        }
      }
    };

    loadIntegrationPrompts();
    return () => {
      isCancelled = true;
    };
  }, [user?.uid, selectedWorkgroup, workgroups]);

  // Fetch sessions function (outside useEffect so it can be called from handleSend)
  const fetchSessions = async () => {
    if (!selectedWorkgroup) return;

    try {
      const token = await getToken();
      const url = `${llmUrl}/v1/getLastSessions?user_id=${user.uid}&workgroup=${selectedWorkgroup}&token=${token}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Error fetching sessions: ${response.status}`);
      }

      const data = await response.json();
      if (data.sessions && data.sessions.length > 0) {
        setSessions(data.sessions);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
      setSessions([]);
    }
  };

  // Fetch sessions when workgroup changes
  useEffect(() => {
    fetchSessions();
  }, [selectedWorkgroup, user.uid]);

  const examplePrompts = useMemo<ExamplePrompt[]>(() => {
    const current = workgroups.find((wg) => wg.id === selectedWorkgroup);
    const enabledIntegrations = Array.isArray(current?.workgroup_types)
      ? current.workgroup_types
      : [];

    const pooledPrompts: ExamplePrompt[] = [];
    for (const integrationId of enabledIntegrations) {
      const integration = integrationMap[integrationId];
      if (!integration) continue;
      const prompts = Array.isArray(integration.suggested_prompts)
        ? integration.suggested_prompts
        : [];
      const integrationLabel = integration.name || integration.id;
      for (const promptText of prompts) {
        const trimmed = (promptText || '').trim();
        if (!trimmed) continue;
        pooledPrompts.push({
          title: trimmed,
          description: integrationLabel ? `From ${integrationLabel}` : undefined,
        });
      }
    }

    if (pooledPrompts.length === 0) {
      return DEFAULT_EXAMPLE_PROMPTS;
    }

    const shuffledPrompts = shuffleArray(pooledPrompts);

    const unique: ExamplePrompt[] = [];
    const seen = new Set<string>();
    for (const prompt of shuffledPrompts) {
      const key = prompt.title.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(prompt);
    }

    if (unique.length >= 4) {
      return unique.slice(0, 4);
    }

    const result = [...unique];
    let index = 0;
    while (result.length < 4 && unique.length > 0) {
      result.push(unique[index % unique.length]);
      index++;
    }

    if (result.length === 0) {
      return DEFAULT_EXAMPLE_PROMPTS;
    }

    while (result.length < 4) {
      result.push(DEFAULT_EXAMPLE_PROMPTS[result.length % DEFAULT_EXAMPLE_PROMPTS.length]);
    }

    return result;
  }, [integrationMap, selectedWorkgroup, workgroups]);

  // Save selected workgroup
  useEffect(() => {
    if (selectedWorkgroup) {
      localStorage.setItem('selectedWorkgroupId', selectedWorkgroup);
    }
  }, [selectedWorkgroup]);

  // Fetch saved prompts when workgroup changes
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        // Fetch personal prompts and groups
        const [personalCards, personalGrps] = await Promise.all([
          fetchPromptCards(null),
          fetchPromptGroups(null)
        ]);
        setPersonalPrompts(personalCards);
        setPersonalGroups(personalGrps);

        // Fetch workgroup prompts and groups if workgroup selected
        if (selectedWorkgroup) {
          const [workgroupCards, workgroupGrps] = await Promise.all([
            fetchPromptCards(selectedWorkgroup),
            fetchPromptGroups(selectedWorkgroup)
          ]);
          setWorkgroupPrompts(workgroupCards);
          setWorkgroupGroups(workgroupGrps);
        } else {
          setWorkgroupPrompts([]);
          setWorkgroupGroups([]);
        }
      } catch (error) {
        console.error("Error loading prompts:", error);
      }
    };

    loadPrompts();
  }, [selectedWorkgroup]);

  // Load user preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await getUserPreferences(user.uid);
        if (prefs) {
          setTheme(prefs.theme || 'light');
          setSendKey(prefs.sendKey || 'Enter');
          setModelSelection(prefs.modelSelection || 0);
          setIpProtection(prefs.ipProtection ?? true);
          setQueryMode(prefs.queryMode || 'standard');
          setRagMode(prefs.ragMode || 'standard');
          setRagK(prefs.ragK || 5);
          setDirectRagMinWords(prefs.directRagMinWords || 3);
        }
      } catch (error) {
        console.error("Error loading preferences:", error);
      }
    };

    loadPreferences();
  }, [user.uid]);

  // Fetch available model options
  useEffect(() => {
    const fetchNumOptions = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${llmUrl}/v1/getNumOptions?token=${token}`);

        if (!response.ok) {
          throw new Error(`Error fetching options: ${response.status}`);
        }

        const data = await response.json();
        if (data.num_options) {
          setMaxModelOptions(data.num_options);
        }
      } catch (error) {
        console.error("Error fetching number of options:", error);
        setMaxModelOptions(3); // Default fallback
      }
    };

    fetchNumOptions();
  }, []);

  // Keep dev access ref in sync for SSE callbacks
  useEffect(() => {
    hasDevAccessRef.current = hasDevAccess;
  }, [hasDevAccess]);

  // Fetch model list with actual names for dev users
  useEffect(() => {
    if (!hasDevAccess) return;

    const fetchModelList = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${llmUrl}/v1/listModels?token=${token}`);
        if (!response.ok) {
          console.warn(`Error fetching models: ${response.status}`);
          return;
        }
        const data = await response.json();
        if (data.models && Array.isArray(data.models)) {
          // Convert string array to ModelInfo array
          const modelInfoList: ModelInfo[] = data.models.map((name: string) => ({ name }));
          setModelList(modelInfoList);
        }
      } catch (error) {
        console.error("Error fetching model list:", error);
      }
    };
    fetchModelList();
  }, [hasDevAccess]);

  // Initialize connection
  const initializeConnection = async () => {
    try {
      const token = await getToken();

      const payload = {
        token: token,
        workgroup: selectedWorkgroup,
        ip_protection: ipProtection,
        integration_ids: ["default-integration"]
      };

      console.log(`Initializing connection for workgroup ${selectedWorkgroup}`);

      const response = await fetch(`${llmUrl}/v1/startConnection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to start connection: ${response.status}`);
      }

      const data = await response.json();
      if (data.session_id) {
        sessionIdRef.current = data.session_id;
        localStorage.setItem('sessionId', data.session_id);
        console.log(`Connection initialized with session_id: ${data.session_id}`);
        return data.session_id;
      }
    } catch (error) {
      console.error("Error initializing connection:", error);
      throw error;
    }
  };

  // Terminate/cancel the current request
  const terminateRun = async () => {
    console.log("🛑 terminateRun called");
    // Mark request as cancelled to prevent response handler from overwriting
    requestCancelledRef.current = true;

    // Abort the in-flight fetch so handleSend stops immediately
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${llmUrl}/v1/terminateRun`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user?.uid,
          session_id: sessionIdRef.current,
          token: token
        }),
      });

      if (response.ok) {
        // Stop loading state
        setIsLoading(false);
        // Add a message indicating the request was stopped
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: `${sessionIdRef.current || 'temp'}-stopped-${Date.now()}`,
            content: "Stopped by user",
            role: "assistant" as const,
            timestamp: Date.now(),
          },
        ]);
      } else {
        console.error("Terminate request failed:", response.status);
        requestCancelledRef.current = false; // Reset on failure
      }
    } catch (error) {
      alert("Could not terminate");
      console.error("Could not terminate:", error);
      requestCancelledRef.current = false; // Reset on error
    }
  };

  // Handle send message with attachments
  const handleSend = async (message?: string, images?: string[], files?: FileMetadata[]) => {
    const messageContent = message || "";
    const attachedImages = images || [];
    const attachedFiles = files || [];

    if (!messageContent.trim() && attachedImages.length === 0 && attachedFiles.length === 0) return;
    if (isLoading) return;

    // Ensure we have a session
    if (!sessionIdRef.current) {
      try {
        await initializeConnection();
        if (!sessionIdRef.current) {
          setError("Cannot connect to server. Please try again.");
          return;
        }
      } catch (err) {
        setError("Failed to connect to server");
        return;
      }
    }

    const userMessage: ChatMessageType = {
      id: `${sessionIdRef.current || 'temp'}-user-${Date.now()}`,
      role: 'user',
      content: messageContent,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);
    requestCancelledRef.current = false; // Reset cancelled flag for new request

    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Capture request start time immediately (before any async operations)
    const requestStartTime = Date.now();
    requestStartTimeRef.current = requestStartTime;

    // Declare eventSource outside try block so it's accessible in catch
    let eventSource: EventSource | null = null;

    try {
      const token = await getToken();

      // Non-dev users always use defaults for RAG settings
      const effectiveQueryMode = hasDevAccessRef.current ? queryMode : 'standard';
      const effectiveRagMode = hasDevAccessRef.current ? ragMode : 'standard';
      const effectiveRagK = hasDevAccessRef.current ? ragK : 5;
      const effectiveDirectRagMinWords = hasDevAccessRef.current ? directRagMinWords : 3;

      // Determine effective mode based on word count for direct RAG
      let effectiveMode = effectiveQueryMode;
      if (effectiveQueryMode === 'direct' && effectiveDirectRagMinWords > 0) {
        const wordCount = messageContent.trim().split(/\s+/).filter(word => word.length > 0).length;
        if (wordCount < effectiveDirectRagMinWords) {
          effectiveMode = 'standard';
          console.log(`Message has ${wordCount} words, below minimum ${effectiveDirectRagMinWords} for direct mode. Using standard mode.`);
        }
      }

      // Prepare payload matching old dashboard format
      const payload: any = {
        messages: [{ role: "user", content: messageContent }],
        session_id: sessionIdRef.current,
        token: token,
        model_selection: modelSelection,
        ip_protection: ipProtection,
        mode: effectiveMode,  // 'standard' or 'direct' (RAG-only mode), may be overridden by word count check
        rag_mode: effectiveRagMode,  // 'standard' or 'agentic' RAG processing mode
        rag_k: effectiveRagK  // Number of RAG chunks to return
      };

      // Only include images if there are any
      if (attachedImages && attachedImages.length > 0) {
        payload.num_images = attachedImages.length;
        payload.images = attachedImages;
      }

      // Only include files if there are any
      if (attachedFiles && attachedFiles.length > 0) {
        payload.files = attachedFiles;
      }

      try {
        // Set up SSE to get status updates
        const statusToken = await getToken();
        setProcessingSteps([]); // Reset steps
        processingStepsRef.current = []; // Reset ref too
        setElapsedTime(0);

        // Start elapsed time counter
        const elapsedInterval = setInterval(() => {
          setElapsedTime(Date.now() - requestStartTime);
        }, 100);

        // Helper to clean up SSE and interval
        const cleanupSSE = () => {
          clearInterval(elapsedInterval);
          if (eventSource) {
            eventSource.close();
          }
          setProcessingSteps([]);
        };

        eventSource = new EventSource(`${llmUrl}/getStatus?session_id=${sessionIdRef.current}&token=${statusToken}`);

        eventSource.onmessage = (event) => {
          try {
            // Try to parse JSON step data
            const data = JSON.parse(event.data);
            setStatus(data.status || 'Processing');

            // Convert steps to TimelineItems
            if (data.steps && Array.isArray(data.steps)) {
              const timelineItems: TimelineItem[] = data.steps.map((step: any, index: number) => ({
                id: `step-${step.num || index}`,
                title: step.status || 'Processing',
                // Only show step details (description, timing) to developers
                description: hasDevAccessRef.current ? (step.description || '') : '',
                status: index === data.steps.length - 1 ? 'active' : 'completed',
                meta: hasDevAccessRef.current ? (step.duration_ms ? `${step.duration_ms}ms` : step.elapsed_ms ? `@ ${step.elapsed_ms}ms` : undefined) : undefined
              }));
              setProcessingSteps(timelineItems);
              processingStepsRef.current = timelineItems; // Keep ref in sync
            }
          } catch {
            // Fallback for plain text status (backwards compatibility)
            setStatus(event.data);
          }
        };
        eventSource.onerror = () => {
          cleanupSSE();
        };

        // Store cleanup function for use outside this block
        (eventSource as any)._cleanup = cleanupSSE;
        (eventSource as any)._elapsedInterval = elapsedInterval;
      } catch (error) {
        console.error("Error setting up status SSE:", error);
        if (eventSource) eventSource.close();
        setStatus("Loading");
      }

      // Send the message with streaming enabled (keeps connection alive with heartbeats)
      const responsePromise = fetch(`${llmUrl}/v1/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Session-Id': sessionIdRef.current
        },
        body: JSON.stringify({ ...payload, stream: true }),
        signal: abortController.signal
      });

      // Now await the response
      const response = await responsePromise;

      // Get model name early (doesn't depend on response body)
      const finalModel = getModelDisplayName(modelSelection, modelList[modelSelection]?.name);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);

        // Clean up SSE on error
        if (eventSource && (eventSource as any)._cleanup) {
          (eventSource as any)._cleanup();
        }

        if (response.status === 402 || errorText.includes("You have used all your available messages for this period")) {
          const messageHtml = `This workgroup has used its daily limits. To increase this, visit your <a class="underline font-semibold" target="_blank" rel="noopener noreferrer" href="${licensePageUrl}">license page</a> or speak to your administrator.`;
          setError(
            <span>
              This workgroup has used its daily limits. To increase this, visit your{" "}
              <a
                href={licensePageUrl}
                className="underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                license page
              </a>{" "}
              or speak to your administrator.
            </span>
          );
          showLicenseToast(messageHtml, 'danger');
          setMessages(prev => prev.slice(0, -1));
          return;
        }

        throw new Error(`Error sending message: ${response.status}`);
      }

      // Handle NDJSON streaming response (newline-delimited JSON)
      // Format: heartbeat lines + final response line
      let data: any = null;
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/x-ndjson')) {
        // Streaming response - read line by line
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (!line.trim()) continue;

              try {
                const parsed = JSON.parse(line);
                if (parsed.type === 'heartbeat') {
                  // Heartbeat - connection is alive, continue waiting
                  console.log(`[Streaming] Heartbeat received, elapsed: ${parsed.elapsed_seconds}s`);
                } else if (parsed.type === 'response') {
                  // Final response received
                  data = parsed.data;
                  if (parsed.status_code !== 200) {
                    throw new Error(`Server returned status ${parsed.status_code}`);
                  }
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.error || 'Unknown streaming error');
                }
              } catch (parseError) {
                console.error('[Streaming] Error parsing line:', line, parseError);
              }
            }
          }

          // Process any remaining buffer
          if (buffer.trim()) {
            try {
              const parsed = JSON.parse(buffer);
              if (parsed.type === 'response') {
                data = parsed.data;
              } else if (parsed.type === 'error') {
                throw new Error(parsed.error || 'Unknown streaming error');
              }
            } catch (parseError) {
              console.error('[Streaming] Error parsing final buffer:', buffer, parseError);
            }
          }
        }

        if (!data) {
          throw new Error('No response received from streaming endpoint');
        }
      } else {
        // Regular JSON response (backwards compatibility)
        data = await response.json();
      }

      // Only process response if it belongs to the current session
      if (data.session_id && data.session_id !== sessionIdRef.current) {
        console.log(`Ignoring response for stale session ${data.session_id}, current session is ${sessionIdRef.current}`);
        return;
      }

      // Don't process response if request was cancelled by user
      if (requestCancelledRef.current) {
        console.log('Ignoring response - request was cancelled by user');
        requestCancelledRef.current = false; // Reset for next request
        return;
      }

      // NOW calculate final elapsed time (after response body is fully processed)
      const finalElapsedTime = requestStartTimeRef.current
        ? (Date.now() - requestStartTimeRef.current) / 1000
        : 0;
      const finalSteps = [...processingStepsRef.current]; // Capture from ref (always current)

      // Clean up SSE and elapsed time interval AFTER response is fully processed
      if (eventSource && (eventSource as any)._cleanup) {
        (eventSource as any)._cleanup();
      }

      const hasOverSeventyPercentLimit = (payload: any): boolean => {
        if (!payload) return false;
        if (payload.over_seventy_percent_limit) return true;
        if (Array.isArray(payload.messages) && payload.messages.some((msg: any) => msg?.over_seventy_percent_limit)) {
          return true;
        }
        if (Array.isArray(payload.choices)) {
          return payload.choices.some((choice: any) => {
            if (!choice) return false;
            if (choice.over_seventy_percent_limit) return true;
            return Boolean(choice.message?.over_seventy_percent_limit);
          });
        }
        return Boolean(payload.choices?.[0]?.message?.over_seventy_percent_limit);
      };

      if (hasOverSeventyPercentLimit(data) && !seventyPercentAlertShownRef.current) {
        showLicenseToast(
          `This workgroup has used 70% of its daily limits. To avoid interruptions, visit your <a class="underline font-semibold" target="_blank" rel="noopener noreferrer" href="${licensePageUrl}">license page</a> or speak to your administrator.`
        );
        seventyPercentAlertShownRef.current = true;
      }

      // Parse response correctly: data.choices[0].message.content
      let llmResponse = data.choices?.[0]?.message?.content;

      // Keep the array format as-is for ChatMessage component to handle
      // No conversion needed - ChatMessage can render text, tables, charts, etc.
      if (!llmResponse) {
        // Fallback to old format if not in choices structure
        llmResponse = data.response || data.message || 'No response received';
      }

      // Get model name from response (backend now includes it)
      const responseModel = data.model || finalModel;

      // Convert processing_log from backend to ProcessingStep format for View Details
      // This is more reliable than SSE steps which may not all be captured
      const processingLog = data.processing_log || [];
      const capturedSteps = processingLog.length > 0
        ? processingLog.map((step: any, index: number) => ({
            step_number: step.metadata?.step_number || index + 1,
            type: step.type || 'processing',  // Preserve the step type for icons
            status: step.type?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'Processing',
            description: step.content || '',
            content: step.content || '',  // Full content for expandable view
            elapsed_ms: step.metadata?.elapsed_ms || 0,
            duration_ms: step.metadata?.duration_ms,
            metadata: step.metadata  // Preserve additional metadata
          }))
        : finalSteps.map((step, index) => ({
            step_number: parseInt(step.id.replace('step-', '')) || index + 1,
            type: step.title?.toLowerCase().replace(/\s+/g, '_') || 'processing',
            status: step.title,
            description: step.description || '',
            content: step.description || '',
            elapsed_ms: parseInt(step.meta?.replace(/[^\d]/g, '') || '0'),
            duration_ms: step.meta?.includes('ms') ? parseInt(step.meta.replace(/[^\d]/g, '')) : undefined
          }));

      const assistantMessage: ChatMessageType = {
        id: `${sessionIdRef.current || 'temp'}-assistant-${Date.now()}`,
        role: 'assistant',
        content: llmResponse,
        timestamp: new Date(),
        metadata: {
          model: responseModel,
          responseTime: finalElapsedTime,
          processingSteps: capturedSteps,
          ragChunkIds: data.rag_chunk_ids || [],
          ragChunksByIntegration: data.rag_chunks_by_integration || {},
          integrations: data.integrations || []
        }
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Refresh sessions if needed
      if (messages.length === 0 || messages.length === 4) {
        fetchSessions();
      }
    } catch (error) {
      // Silently ignore aborted requests (user cancelled or started new chat)
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Request aborted by user');
        // Clean up SSE on abort
        if (eventSource && (eventSource as any)._cleanup) {
          (eventSource as any)._cleanup();
        }
        return;
      }
      console.error('Error sending message:', error);
      // Clean up SSE on exception
      if (eventSource && (eventSource as any)._cleanup) {
        (eventSource as any)._cleanup();
      }
      if (!`${error}`.includes("daily limit")) {
        setError('Failed to send message. Please try again.');
        // Remove the user message if send failed
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Close session
  const closeSession = async () => {
    if (!sessionIdRef.current) {
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`${llmUrl}/v1/closeSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionIdRef.current,
          token: token
        }),
      });

      if (response.ok) {
        console.log("Session closed successfully");
      } else {
        console.warn(`Server returned status ${response.status}`);
      }
    } catch (error) {
      console.warn("Error closing session (ignored):", error);
    }
  };

  // Handle keyboard shortcuts
  // Start new chat
  const handleNewChat = async () => {
    await closeSession();
    setMessages([]);
    sessionIdRef.current = null;
    localStorage.removeItem('sessionId');
    localStorage.removeItem('isRecoveredChat');
  };

  // Delete an existing chat session
  const handleDeleteSession = async (sessionId: string) => {
    if (!sessionId) return;
    const confirmed = window.confirm('Delete this chat? This cannot be undone.');
    if (!confirmed) return;

    try {
      const token = await getToken();
      const response = await fetch(`${llmUrl}/v1/deleteSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          session_id: sessionId,
          token
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete session: ${response.status}`);
      }

      // Remove from sidebar list immediately
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));

      // If the current chat was deleted, clear state and start fresh
      if (sessionIdRef.current === sessionId) {
        sessionIdRef.current = null;
        localStorage.removeItem('sessionId');
        localStorage.removeItem('isRecoveredChat');
        setMessages([]);
        setStatus('Loading');
        await initializeConnection();
      }
    } catch (error) {
      console.error("Error deleting session:", error);
      setError("Failed to delete chat. Please try again.");
    }
  };

  // Vote on an assistant message
  const handleVote = async (messageId: string, vote: 'up' | 'down') => {
    const msgIndex = messages.findIndex(m => m.id === messageId);
    const msg = msgIndex >= 0 ? messages[msgIndex] : undefined;
    if (!msg?.metadata) return;

    // Find the preceding user query for this assistant message
    const userQuery = msgIndex > 0 ? messages.slice(0, msgIndex).reverse().find(m => m.role === 'user')?.content : undefined;

    // Toggle: clicking same vote again removes it
    const newVote = msg.metadata.vote === vote ? undefined : vote;

    // Update local state immediately
    setMessages(prev => prev.map(m =>
      m.id === messageId
        ? { ...m, metadata: { ...m.metadata!, vote: newVote } }
        : m
    ));

    // Only call API if setting a vote (not removing)
    if (newVote) {
      try {
        const token = await getToken();
        await fetch(`${llmUrl}/v1/voteSession`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            user_id: user.uid,
            vote: newVote,
            workgroup: selectedWorkgroup,
            model: msg.metadata.model,
            processing_time: msg.metadata.responseTime,
            integrations: msg.metadata.integrations || [],
            query: userQuery,
            token
          }),
        });
      } catch (error) {
        console.error("Error voting on session:", error);
      }
    }
  };

  // Reload prompts
  const reloadPrompts = async () => {
    try {
      const [personalCards, workgroupCards] = await Promise.all([
        fetchPromptCards(null),
        selectedWorkgroup ? fetchPromptCards(selectedWorkgroup) : Promise.resolve([])
      ]);
      setPersonalPrompts(personalCards);
      setWorkgroupPrompts(workgroupCards);
    } catch (error) {
      console.error("Error reloading prompts:", error);
    }
  };

  // Create/Edit prompt handler
  const handleSavePrompt = async () => {
    if (!promptTitle.trim() || !promptQuery.trim()) {
      setError("Title and query are required");
      return;
    }

    try {
      const promptData = {
        title: promptTitle,
        query: promptQuery
      };

      if (editingPrompt) {
        // Update existing prompt
        await updatePromptCard(
          editingPrompt.id,
          promptData,
          isWorkgroupPrompt ? selectedWorkgroup : null
        );
      } else {
        // Create new prompt
        await createPromptCard(
          promptData,
          isWorkgroupPrompt ? selectedWorkgroup : null
        );
      }

      // Reload prompts
      await reloadPrompts();

      // Close dialog and reset
      setShowPromptDialog(false);
      setEditingPrompt(null);
      setPromptTitle("");
      setPromptQuery("");
    } catch (error) {
      console.error("Error saving prompt:", error);
      setError("Failed to save prompt");
    }
  };

  // Delete prompt handler
  const handleDeletePrompt = (promptId: string, isWorkgroup: boolean) => {
    setPromptToDelete({ id: promptId, isWorkgroup });
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const confirmDeletePrompt = async () => {
    if (!promptToDelete) return;

    try {
      await deletePromptCard(
        promptToDelete.id,
        promptToDelete.isWorkgroup ? selectedWorkgroup : null
      );
      await reloadPrompts();
      setPromptToDelete(null);
    } catch (error) {
      console.error("Error deleting prompt:", error);
      setError("Failed to delete prompt");
    }
  };

  // Open dialog for new prompt
  const handleNewPrompt = (isWorkgroup: boolean) => {
    setEditingPrompt(null);
    setIsWorkgroupPrompt(isWorkgroup);
    setPromptTitle("");
    setPromptQuery("");
    setShowPromptDialog(true);
  };

  // Promote a user message into a new prompt card
  const handlePromoteMessageToPrompt = (content: string) => {
    const trimmedContent = (content || '').trim();
    if (!trimmedContent) return;

    const firstLine = trimmedContent
      .split('\n')
      .find(line => line.trim().length > 0) || trimmedContent;
    const maxTitleLength = 80;
    const normalizedTitle = firstLine.length > maxTitleLength
      ? `${firstLine.slice(0, maxTitleLength)}...`
      : firstLine;

    setEditingPrompt(null);
    setIsWorkgroupPrompt(Boolean(selectedWorkgroup));
    setPromptTitle(normalizedTitle);
    setPromptQuery(trimmedContent);
    setShowPromptDialog(true);
  };

  // Open dialog for editing
  const handleEditPrompt = (prompt: PromptCard, isWorkgroup: boolean) => {
    setEditingPrompt(prompt);
    setIsWorkgroupPrompt(isWorkgroup);
    setPromptTitle(prompt.title);
    setPromptQuery(prompt.query);
    setShowPromptDialog(true);
  };

  // Load a previous session
  const loadSession = async (sessionId: string) => {
    try {
      const token = await getToken();
      const url = `${llmUrl}/v1/recoverChat?session_id=${sessionId}&token=${token}`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Error loading session: ${response.status}`);
      }

      const data = await response.json();
      const history = Array.isArray(data.chat_history)
        ? data.chat_history
        : Array.isArray(data.messages)
          ? data.messages
          : [];

      const parseTimestamp = (raw: any) => {
        if (!raw) return new Date();
        if (typeof raw === 'number' || typeof raw === 'string') {
          const candidate = new Date(raw);
          return isNaN(candidate.getTime()) ? new Date() : candidate;
        }
        if (typeof raw === 'object' && typeof raw.seconds === 'number') {
          const nanos = raw.nanoseconds || raw.nanos || 0;
          return new Date(raw.seconds * 1000 + Math.floor(nanos / 1e6));
        }
        return new Date();
      };

      const formattedMessages: ChatMessageType[] = history.map((msg: any, index: number) => {
        const timestamp = parseTimestamp(msg.timestamp);
        const normalizedRole =
          msg.role ||
          (msg.type === 'human'
            ? 'user'
            : msg.type === 'assistant'
              ? 'assistant'
              : 'system');

        const normalizedMessage: ChatMessageType = {
          id: typeof msg.id === 'string' ? msg.id : `${sessionId}-${index}`,
          role: normalizedRole,
          content: msg.content ?? msg.message ?? '',
          timestamp
        };

        if (msg.images) {
          normalizedMessage.images = msg.images;
        }

        if (msg.specialBlocks) {
          normalizedMessage.specialBlocks = msg.specialBlocks;
        }

        return normalizedMessage;
      });

      setMessages(formattedMessages);
      sessionIdRef.current = sessionId;
      localStorage.setItem('sessionId', sessionId);
      localStorage.setItem('isRecoveredChat', 'true');
    } catch (error) {
      console.error('Error loading session:', error);
      setError('Failed to load session');
    }
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="w-[280px] border-r bg-card flex flex-col"
          >
            {/* New Chat Button */}
            <div className="p-3">
              <Button
                onClick={handleNewChat}
                className="w-full justify-start gap-2"
                variant="outline"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </Button>
            </div>

            {/* Workgroup Selector */}
            <div className="px-3 py-2 space-y-2">
              <WorkgroupSelect
                workgroups={workgroups}
                selectedWorkgroup={selectedWorkgroup}
                onSelect={setSelectedWorkgroup}
              />

              {maxModelOptions > 1 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground px-1">Model</label>
                  <Select
                    value={modelSelection.toString()}
                    onChange={(e) => handleModelPreferenceChange(parseInt(e.target.value))}
                    className="mt-1"
                    data-testid="model-select"
                  >
                    {Array.from({ length: maxModelOptions }, (_, i) => (
                      <option key={i} value={i} data-testid={`model-option-${i}`}>
                        {getModelOptionLabel(i)}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-2">
              {/* Saved Prompts Section */}
              <div>
                {/* Personal Prompts */}
                <div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <button
                      onClick={() => setIsPersonalPromptsCollapsed(!isPersonalPromptsCollapsed)}
                      className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span>My Prompts</span>
                      {isPersonalPromptsCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleNewPrompt(false)}
                      title="New personal prompt"
                    >
                      <BookmarkPlus className="w-3 h-3" />
                    </Button>
                  </div>
                  {!isPersonalPromptsCollapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      {personalPrompts.length > 0 ? (
                        <div className="space-y-1">
                          {personalPrompts.map(prompt => (
                            <div
                              key={prompt.id}
                              className="group relative px-3 py-2 rounded-lg hover:bg-accent transition-colors"
                            >
                              <div
                                onClick={() => handleSend(prompt.query, [], [])}
                                className="cursor-pointer pr-16"
                              >
                                <div className="text-sm font-medium truncate">
                                  {prompt.title}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {prompt.query.substring(0, 40)}...
                                </div>
                              </div>
                              <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 flex gap-1 z-10">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 pointer-events-auto"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleEditPrompt(prompt, false);
                                  }}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-destructive pointer-events-auto"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleDeletePrompt(prompt.id, false);
                                  }}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-3 py-2 text-xs text-muted-foreground">
                          No personal prompts yet
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                {/* Workgroup Prompts */}
                {selectedWorkgroup && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between px-3 py-2">
                      <button
                        onClick={() => setIsWorkgroupPromptsCollapsed(!isWorkgroupPromptsCollapsed)}
                        className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <span>Workgroup Prompts</span>
                        {isWorkgroupPromptsCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleNewPrompt(true)}
                        title="New workgroup prompt"
                      >
                        <BookmarkPlus className="w-3 h-3" />
                      </Button>
                    </div>
                    {!isWorkgroupPromptsCollapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        {workgroupPrompts.length > 0 ? (
                          <div className="space-y-1">
                            {workgroupPrompts.map(prompt => (
                              <div
                                key={prompt.id}
                                className="group relative px-3 py-2 rounded-lg hover:bg-accent transition-colors"
                              >
                                <div
                                  onClick={() => handleSend(prompt.query, [], [])}
                                  className="cursor-pointer pr-16"
                                >
                                  <div className="text-sm font-medium truncate">
                                    {prompt.title}
                                  </div>
                                  <div className="text-xs text-muted-foreground truncate">
                                    {prompt.query.substring(0, 40)}...
                                  </div>
                                </div>
                                <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 flex gap-1 z-10">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 pointer-events-auto"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleEditPrompt(prompt, true);
                                    }}
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-destructive pointer-events-auto"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleDeletePrompt(prompt.id, true);
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="px-3 py-2 text-xs text-muted-foreground">
                            No workgroup prompts yet
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                )}
              </div>

              {/* Chat History */}
              <div className="mt-4">
                <button
                  onClick={() => setIsChatsCollapsed(!isChatsCollapsed)}
                  className="flex items-center justify-between w-full text-xs font-medium text-muted-foreground px-3 py-2 hover:bg-accent rounded transition-colors"
                >
                  <span>Recent Chats</span>
                  {isChatsCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {!isChatsCollapsed && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-1">
                      {sessions.map(session => (
                        <div
                          key={session.session_id}
                          className="relative px-3 py-2 rounded-lg hover:bg-accent transition-colors group"
                        >
                          <div
                            className="flex items-center gap-2 cursor-pointer pr-10"
                            onClick={() => loadSession(session.session_id)}
                          >
                            <MessageSquare className="w-4 h-4 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {session.title || session.first_message || 'Untitled Chat'}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {session.first_message?.substring(0, 50) || 'No preview'}
                              </div>
                            </div>
                          </div>
                          <button
                            className="absolute right-2 top-2 h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleDeleteSession(session.session_id);
                            }}
                            title="Delete chat"
                            aria-label="Delete chat"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* User Menu */}
            <div className="p-3 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user?.email?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {user?.email || 'User'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSettings(true)}
                    title="Settings"
                  >
                    <SettingsIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Logout"
                    onClick={onLogout}
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Error Banner */}
        {error && (
          <div className="bg-destructive/10 border-b border-destructive/20 px-4 py-2 text-sm text-destructive">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto">
          {messages.length === 0 ? (
            // Empty State
            <div className="h-full flex items-center justify-center px-4">
              <div className="max-w-2xl w-full">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <img src="/icon_dark.svg" alt="Interpretos" className="w-8 h-8" />
                  </div>
                  <h2 className="text-2xl font-semibold mb-2">
                    Welcome to Interpretos
                  </h2>
                  <p className="text-muted-foreground">
                    Ask questions about your enterprise data and get instant answers
                  </p>
                </div>

                {/* Example Prompts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {examplePrompts.map((example, i) => (
                    <Card
                      key={i}
                      className="p-4 cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => handleSend(example.title, [], [])}
                    >
                      <div className="font-medium text-sm mb-1">
                        {example.title}
                      </div>
                      {example.description && (
                        <div className="text-xs text-muted-foreground">
                          {example.description}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            // Messages
            <div className="max-w-3xl mx-auto w-full px-4 py-6 space-y-6">
              {messages.map((message, i) => (
                <ChatMessage
                  key={i}
                  message={message}
                  theme={theme as 'light' | 'dark'}
                  hasDevAccess={hasDevAccess}
                  onPromoteToPrompt={handlePromoteMessageToPrompt}
                  onVote={message.role === 'assistant' ? (vote) => handleVote(message.id, vote) : undefined}
                />
              ))}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-4"
                >
                  <div className="w-8 h-8 flex items-center justify-center mt-1">
                    <img src="/icon_dark.svg" alt="Interpretos" className="w-6 h-6" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-3 min-w-[280px]">
                    {processingSteps.length > 0 ? (
                      <div className="space-y-3">
                        <StatusTimeline items={processingSteps} dense />
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
                          <span>Elapsed: <span className="font-mono text-primary">{(elapsedTime / 1000).toFixed(1)}s</span></span>
                          <span className="text-muted-foreground/70">Step {processingSteps.length}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        {status && status !== 'Loading' && (
                          <span className="text-sm text-muted-foreground">{status}</span>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t bg-card">
          <div className="max-w-3xl mx-auto w-full p-4">
            {/* Query Mode Toggle - only visible to dev users */}
            {hasDevAccess && (
            <div className="flex items-center justify-center mb-3 gap-4 flex-wrap" data-testid="query-mode-toggle">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Mode:</span>
                <div className="inline-flex rounded-lg p-0.5 bg-muted">
                  <button
                    onClick={() => handleQueryModeChange('standard')}
                    data-testid="mode-standard-btn"
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      queryMode === 'standard'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => handleQueryModeChange('direct')}
                    data-testid="mode-direct-btn"
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      queryMode === 'direct'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    title="Query knowledge base directly without AI processing"
                  >
                    Direct
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">RAG:</span>
                <div className="inline-flex rounded-lg p-0.5 bg-muted">
                  <button
                    onClick={() => handleRagModeChange('standard')}
                    data-testid="rag-mode-standard-btn"
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      ragMode === 'standard'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    title="Standard RAG processing"
                  >
                    Standard
                  </button>
                  <button
                    onClick={() => handleRagModeChange('agentic')}
                    data-testid="rag-mode-agentic-btn"
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      ragMode === 'agentic'
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    title="Agentic RAG processing with enhanced reasoning"
                  >
                    Agentic
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Chunks:</span>
                <select
                  value={ragK}
                  onChange={(e) => handleRagKChange(Number(e.target.value))}
                  data-testid="rag-k-select"
                  className="px-2 py-1 text-xs font-medium rounded-md bg-muted border-0 text-foreground focus:ring-1 focus:ring-primary"
                  title="Number of knowledge base chunks to retrieve"
                >
                  {[1, 2, 3, 5, 10, 15, 20].map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
              {queryMode === 'direct' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Min Words:</span>
                  <select
                    value={directRagMinWords}
                    onChange={(e) => handleDirectRagMinWordsChange(Number(e.target.value))}
                    data-testid="direct-rag-min-words-select"
                    className="px-2 py-1 text-xs font-medium rounded-md bg-muted border-0 text-foreground focus:ring-1 focus:ring-primary"
                    title="Minimum words for direct RAG mode (shorter queries use standard mode)"
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 10].map((w) => (
                      <option key={w} value={w}>{w === 0 ? 'Off' : w}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            )}
            <ChatInput
              onSend={(message, images, files) => {
                handleSend(message, images, files);
                return true; // Indicate success
              }}
              theme={theme as any}
              sendKey={sendKey}
              isLoading={isLoading}
              onCancel={terminateRun}
              inputRef={textareaRef}
            />
          </div>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent onClose={() => setShowSettings(false)}>
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
            <DialogDescription>
              Customize your Interpretos experience
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Theme Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Theme</label>
              <Select value={theme} onChange={(e) => handleThemePreferenceChange(e.target.value)}>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </Select>
            </div>

            {/* Send Key Preference */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Send Message With</label>
              <Select value={sendKey} onChange={(e) => handleSendKeyPreferenceChange(e.target.value)}>
                <option value="Enter">Enter</option>
                <option value="Shift+Enter">Shift+Enter</option>
              </Select>
              <p className="text-xs text-muted-foreground">
                {sendKey === 'Enter'
                  ? 'Press Enter to send, Shift+Enter for new line'
                  : 'Press Shift+Enter to send, Enter for new line'}
              </p>
            </div>

            {/* IP Protection */}
            {hasDevAccess && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <label className="text-sm font-medium">IP Protection</label>
                  <p className="text-xs text-muted-foreground">
                    Anonymize your IP address in requests
                  </p>
                </div>
                <button
                  onClick={handleIpProtectionChange}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    ipProtection ? "bg-primary" : "bg-muted"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      ipProtection ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
              <Button onClick={async () => {
                const success = await persistPreferences();
                if (success) {
                  setShowSettings(false);
                } else {
                  setError("Failed to save settings");
                }
              }}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prompt Create/Edit Dialog */}
      <Dialog open={showPromptDialog} onOpenChange={setShowPromptDialog}>
        <DialogContent onClose={() => setShowPromptDialog(false)}>
          <DialogHeader>
            <DialogTitle>
              {editingPrompt ? 'Edit' : 'New'} {isWorkgroupPrompt ? 'Workgroup' : 'Personal'} Prompt
            </DialogTitle>
            <DialogDescription>
              {editingPrompt ? 'Update' : 'Create'} a saved prompt for quick access
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <input
                type="text"
                value={promptTitle}
                onChange={(e) => setPromptTitle(e.target.value)}
                placeholder="e.g., Sales Pipeline Analysis"
                className="w-full px-3 py-2 text-sm rounded-md border bg-background"
              />
            </div>

            {/* Query */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Prompt Query</label>
              <Textarea
                value={promptQuery}
                onChange={(e) => setPromptQuery(e.target.value)}
                placeholder="Enter the prompt text..."
                className="min-h-[120px] resize-none"
                rows={6}
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPromptDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePrompt}>
                {editingPrompt ? 'Update' : 'Create'} Prompt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={confirmDeletePrompt}
        title="Delete Prompt"
        description="Are you sure you want to delete this prompt? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
};
